import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge, Modal, Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { gameService } from '../../services/gameService';
import { GameSpaceInfo, HostInfo } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useGameSpace } from '../../contexts/GameSpaceContext';

const GameSpaceList: React.FC = () => {
  const { gameSpaces, loading, error, refreshGameSpaces } = useGameSpace();
  const [hostInfo, setHostInfo] = useState<HostInfo | null>(null);
  const [userType, setUserType] = useState<string>('');  // To store user type/role
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [currentGameSpace, setCurrentGameSpace] = useState<GameSpaceInfo | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isPublic: false,
    maxPlayers: 10,
    allowExternalHost: false
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    description: '',
    maxPlayers: ''
  });
  const [updateSuccess, setUpdateSuccess] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Helper function to determine if user can edit a game space
  const canEditGameSpace = (gameSpace: GameSpaceInfo): boolean => {
    console.log('Checking edit permissions for game space:', gameSpace);
    console.log('Current user type:', userType);
    console.log('Host info:', hostInfo);

    // First check if user exists
    if (!user || !hostInfo) return false;
    
    // Check if user is the host of this game space
    if (hostInfo.isHost || hostInfo.isProUser) {
      // For Host/Pro users, they can edit their own game spaces
      // Since the game spaces list already shows only the user's own spaces,
      // they should be able to edit any game space in this list
      return true;
    }
    
    // Guest hosts cannot edit game spaces, even if they can host games in them
    // Per updated requirements, only actual hosts can edit game spaces
    
    return false;
  };

  useEffect(() => {
    // Fetch host info and determine user type/role
    const fetchUserInfo = async () => {
      try {
        const hostResponse = await gameService.getHostInfo();
        if (hostResponse.success) {
          setHostInfo(hostResponse.data);
          
          // Set user type based on response
          if (hostResponse.data.isProUser) {
            setUserType('Pro');
          } else if (hostResponse.data.isHost) {
            setUserType('Host');
          } else if (hostResponse.data.isGuestHost) {
            setUserType('Guest Host');
          } else {
            setUserType('Regular');
          }
        }
      } catch (err) {
        console.error('Error fetching host info:', err);
      }
    };

    if (user) {
      fetchUserInfo();
    }
  }, [user]);

  const handleEditClick = (gameSpace: GameSpaceInfo) => {
    console.log('Edit button clicked for game space:', gameSpace);
    setCurrentGameSpace(gameSpace);
    
    // Fix for name vs title inconsistency
    setFormData({
      name: gameSpace.name || gameSpace.title || '',
      description: gameSpace.description || '',
      isPublic: gameSpace.isPublic || false,
      maxPlayers: gameSpace.maxPlayers || 10,
      allowExternalHost: gameSpace.allowExternalHost || false
    });
    
    setShowEditModal(true);
    // Clear any previous messages
    setUpdateSuccess(null);
    setUpdateError(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const validateForm = () => {
    const errors = {
      name: '',
      description: '',
      maxPlayers: ''
    };
    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    } else if (formData.name.trim().length < 3) {
      errors.name = 'Name must be at least 3 characters';
      isValid = false;
    } else if (formData.name.trim().length > 50) {
      errors.name = 'Name must be less than 50 characters';
      isValid = false;
    }

    if (formData.description && formData.description.trim().length > 500) {
      errors.description = 'Description must be less than 500 characters';
      isValid = false;
    }

    const maxPlayersNum = Number(formData.maxPlayers);
    if (isNaN(maxPlayersNum) || maxPlayersNum < 2) {
      errors.maxPlayers = 'Max players must be at least 2';
      isValid = false;
    } else if (maxPlayersNum > 100) {
      errors.maxPlayers = 'Max players cannot exceed 100';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleUpdateGameSpace = async () => {
    if (!currentGameSpace) {
      console.error('No game space selected for update');
      return;
    }
    
    setUpdateError(null);
    setUpdateSuccess(null);
    
    // Validate form before submitting
    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }
    
    console.log('Updating game space:', currentGameSpace.id, formData);
    
    try {
      // Note: The API expects a title field, but our form uses name
      const response = await gameService.updateGameSpace(currentGameSpace.id.toString(), {
        name: formData.name, // This might map to title in the API
        description: formData.description,
        isPublic: formData.isPublic,
        maxPlayers: Number(formData.maxPlayers),
        allowExternalHost: formData.allowExternalHost
      });
      
      console.log('Update response:', response);
      
      if (response.success) {
        setUpdateSuccess('Game space updated successfully!');
        // Refresh game spaces to get the updated data
        await refreshGameSpaces();
        console.log('Game spaces refreshed');
        
        // Close the modal after a short delay
        setTimeout(() => {
          setShowEditModal(false);
        }, 1500);
      } else {
        setUpdateError(response.message || 'Failed to update game space');
      }
    } catch (err) {
      setUpdateError('An unexpected error occurred while updating the game space');
      console.error('Error updating game space:', err);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading your game spaces...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col md={8}>
          <h1 className="display-5 fw-bold">
            <i className="fas fa-dice me-3 text-primary"></i>
            My GameSpaces
          </h1>
          <p className="lead">
            Manage your game spaces and create new trivia experiences
          </p>
        </Col>
        {hostInfo && (
          <Col md={4} className="text-end">
            <Card className="shadow-sm">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-bold">Account Status:</span>
                  <Badge bg={hostInfo.isProUser ? "success" : "secondary"} className="ms-2">
                    {hostInfo.isProUser ? "PRO" : "STANDARD"}
                  </Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold">Token Balance:</span>
                  <Badge bg="primary" className="ms-2">
                    <i className="fas fa-coins me-1"></i> {hostInfo.tokenBalance}
                  </Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {gameSpaces.length === 0 && !loading && !error ? (
        <Alert variant="info">
          <Alert.Heading>No GameSpaces Found</Alert.Heading>
          <p>
            You haven't created any game spaces yet. Create your first game space to start hosting trivia games!
          </p>
          <hr />
          <div className="d-flex justify-content-end">
            <Button variant="outline-primary">Create GameSpace</Button>
          </div>
        </Alert>
      ) : (
        <Row xs={1} md={2} lg={3} className="g-4">
          {gameSpaces.map((gameSpace) => (
            <Col key={gameSpace.id}>
              <Card className="h-100 shadow-sm">
                <Card.Header className="bg-primary bg-gradient text-white">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">{gameSpace.title}</h5>
                    <Badge bg={gameSpace.isPublic ? "success" : "secondary"}>
                      {gameSpace.isPublic ? "Public" : "Private"}
                    </Badge>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Card.Text>
                    {gameSpace.description || "No description provided."}
                  </Card.Text>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <small className="text-muted">
                      <i className="fas fa-gamepad me-1"></i> {gameSpace.gameCount} games
                    </small>
                    <small className="text-muted">
                      <i className="fas fa-users me-1"></i> Max {gameSpace.maxPlayers} players
                    </small>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      Created: {new Date(gameSpace.createdAt).toLocaleDateString()}
                    </small>
                    <Badge bg={gameSpace.allowExternalHost ? "info" : "warning"} className="me-2">
                      {gameSpace.allowExternalHost ? "External Hosting" : "Owner Hosting Only"}
                    </Badge>
                  </div>
                </Card.Body>
                <Card.Footer className="bg-white">
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">{new Date(gameSpace.createdAt).toLocaleDateString()}</small>
                    <div className="d-flex gap-2">
                      {/* Only show Edit button if user has permission */}
                      {canEditGameSpace(gameSpace) && (
                        <Button
                          onClick={(e) => {
                            e.preventDefault();
                            handleEditClick(gameSpace);
                          }}
                          variant="outline-secondary"
                          title="Edit game space"
                          className="me-2"
                        >
                          <i className="fas fa-edit"></i> Edit
                        </Button>
                      )}
                      <Button 
                        onClick={() => navigate(`/gamespace/${gameSpace.id}`)}
                        variant="primary"
                      >
                        <i className="fas fa-door-open me-2"></i>
                        Enter
                      </Button>
                    </div>
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Edit Game Space Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Game Space</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {updateSuccess && <Alert variant="success">{updateSuccess}</Alert>}
          {updateError && <Alert variant="danger">{updateError}</Alert>}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                isInvalid={!!formErrors.name}
              />
              {formErrors.name && <Form.Control.Feedback type="invalid">{formErrors.name}</Form.Control.Feedback>}
              <Form.Text className="text-muted">3-50 characters</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                isInvalid={!!formErrors.description}
              />
              {formErrors.description && <Form.Control.Feedback type="invalid">{formErrors.description}</Form.Control.Feedback>}
              <Form.Text className="text-muted">Up to 500 characters</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Maximum Players <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="number"
                name="maxPlayers"
                value={formData.maxPlayers}
                onChange={handleInputChange}
                min={2}
                max={100}
                isInvalid={!!formErrors.maxPlayers}
              />
              {formErrors.maxPlayers && <Form.Control.Feedback type="invalid">{formErrors.maxPlayers}</Form.Control.Feedback>}
              <Form.Text className="text-muted">Between 2 and 100 players</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Public Game Space"
                name="isPublic"
                checked={formData.isPublic}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                Public game spaces can be discovered by other users
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Allow External Hosts"
                name="allowExternalHost"
                checked={formData.allowExternalHost}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                Allow other users to host games in this game space
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleUpdateGameSpace}
            disabled={updateSuccess !== null}
          >
            {updateSuccess ? 'Saved!' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default GameSpaceList;
